/* --------------------
   FTT SHIP METER — free-shipping progress bar · v1.0
   Mounts into the Shrine PRO cart drawer and stays in sync with
   the cart. Config comes from sections/ftt-ship-meter.liquid.

   Why JS and not a Liquid render inside sections/cart-drawer.liquid:
   Shrine's CartDrawer replaces the whole innerHTML of #CartDrawer on
   every cart change (getSectionsToRender -> selector "#CartDrawer"),
   and that section file is ~94KB of heavily customised theme code.
   This module wraps CartDrawer.prototype.renderContents so the meter
   is re-seated synchronously, before paint — no flicker, no surgery.
   -------------------- */

(function () {
  'use strict';

  var CFG = window.FTTShipMeterConfig || {};
  if (CFG.enabled === false) return;

  var THRESHOLDS = CFG.thresholds || {};
  var FALLBACK = CFG.fallback || { code: 'AUD', amount: 200 };
  var NEAR = typeof CFG.near === 'number' ? CFG.near : 0.7;
  var COPY = CFG.copy || {};

  var LOCALES = {
    AUD: 'en-AU', NZD: 'en-NZ', CAD: 'en-CA', USD: 'en-US', GBP: 'en-GB',
    EUR: 'en-IE', SGD: 'en-SG', HKD: 'en-HK', JPY: 'ja-JP', INR: 'en-IN',
    CHF: 'de-CH', DKK: 'da-DK', SEK: 'sv-SE', PLN: 'pl-PL', AED: 'en-AE',
    ILS: 'he-IL', KRW: 'ko-KR', MYR: 'ms-MY'
  };
  var SYMBOLS = { AUD: '$', NZD: '$', CAD: '$', USD: '$', GBP: '£', EUR: '€', JPY: '¥' };
  var NO_DECIMALS = { JPY: 1, KRW: 1 };

  var REDUCE = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  var el = null, fill = null, label = null, target = null;
  var cart = null;
  var lastPct = 0, lastState = '', lastRemaining = null;
  var inflight = null, timer = null, tween = null, popTimer = null;

  /* -------------------- money -------------------- */

  function fmt(cents, cur) {
    var amt = (cents || 0) / 100;
    var dp = NO_DECIMALS[cur] ? 0 : (Math.abs(amt % 1) < 0.005 ? 0 : 2);
    try {
      return new Intl.NumberFormat(LOCALES[cur] || 'en-AU', {
        style: 'currency',
        currency: cur,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: dp,
        maximumFractionDigits: dp
      }).format(amt);
    } catch (e) {
      return (SYMBOLS[cur] || cur + ' ') + amt.toFixed(dp);
    }
  }

  /* -------------------- maths -------------------- */

  function activeCurrency() {
    var c = (cart && cart.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      FALLBACK.code;
    return String(c).toUpperCase();
  }

  function compute() {
    var cur = activeCurrency();
    var mapped = THRESHOLDS[cur];
    var rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1');
    var thresholdCents;

    if (mapped != null) {
      thresholdCents = Math.round(parseFloat(mapped) * 100);
    } else if (rate > 0 && Math.abs(rate - 1) > 0.0001) {
      // Unmapped currency, but Shopify exposed a real FX rate — convert the
      // Rest-of-World threshold (shop currency) into what the shopper sees.
      thresholdCents = Math.round(FALLBACK.amount * rate * 100);
    } else {
      // No threshold and no usable rate: never fake a bar, state the rule.
      return { flat: true, currency: FALLBACK.code, thresholdCents: Math.round(FALLBACK.amount * 100) };
    }

    var total = cart ? cart.total_price || 0 : 0;
    var remaining = Math.max(0, thresholdCents - total);
    return {
      flat: false,
      currency: cur,
      thresholdCents: thresholdCents,
      total: total,
      remaining: remaining,
      pct: thresholdCents > 0 ? Math.min(1, total / thresholdCents) : 0,
      done: remaining <= 0
    };
  }

  /* -------------------- DOM -------------------- */

  function build() {
    var w = document.createElement('div');
    w.className = 'ftt-sm';
    w.setAttribute('data-ftt-sm', '');
    w.innerHTML =
      '<div class="ftt-sm__row">' +
        '<span class="ftt-sm__dot" aria-hidden="true"></span>' +
        '<span class="ftt-sm__label" role="status" aria-live="polite"></span>' +
        '<span class="ftt-sm__target"></span>' +
      '</div>' +
      '<div class="ftt-sm__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">' +
        '<span class="ftt-sm__fill"><i class="ftt-sm__spark"></i></span>' +
      '</div>';
    return w;
  }

  function host() {
    var drawer = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');
    if (!drawer) return null;
    return drawer.querySelector('.cart-drawer__body') || drawer.querySelector('.drawer__inner') || null;
  }

  function mount() {
    if (el && el.isConnected) return true;
    var h = host();
    if (!h) return false;
    el = build();
    fill = el.querySelector('.ftt-sm__fill');
    label = el.querySelector('.ftt-sm__label');
    target = el.querySelector('.ftt-sm__target');
    h.insertBefore(el, h.firstChild);
    return true;
  }

  /* -------------------- copy -------------------- */

  function setLabel(tpl, amountStr, thresholdStr) {
    var text = String(tpl == null ? '' : tpl).split('[threshold]').join(thresholdStr || '');
    var parts = text.split('[amount]');
    label.textContent = '';
    for (var i = 0; i < parts.length; i++) {
      label.appendChild(document.createTextNode(parts[i]));
      if (i < parts.length - 1) {
        var b = document.createElement('b');
        b.className = 'ftt-sm__amt';
        b.textContent = amountStr;
        label.appendChild(b);
      }
    }
  }

  /* Count the remaining figure down/up instead of snapping — small detail,
     does most of the "premium" work. */
  function tweenAmount(from, to, cur) {
    if (tween) cancelAnimationFrame(tween);
    var node = label.querySelector('.ftt-sm__amt');
    if (!node || REDUCE || from == null || from === to) return;
    var t0 = performance.now(), dur = 480;
    (function step(now) {
      var p = Math.min(1, (now - t0) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      node.textContent = fmt(Math.round(from + (to - from) * eased), cur);
      if (p < 1) tween = requestAnimationFrame(step);
    })(t0);
  }

  /* -------------------- render -------------------- */

  function paint(pct, reseat) {
    if (reseat) {
      el.classList.add('ftt-sm--warp');
      fill.style.width = (lastPct * 100).toFixed(2) + '%';
      void fill.offsetWidth;
      el.classList.remove('ftt-sm--warp');
    }
    fill.style.width = (pct * 100).toFixed(2) + '%';
    var track = el.querySelector('.ftt-sm__track');
    if (track) track.setAttribute('aria-valuenow', Math.round(pct * 100));
    lastPct = pct;
  }

  function render(reseat) {
    if (!el || !el.isConnected) return;

    var count = cart ? cart.item_count || 0 : 0;
    if (!count) {
      el.hidden = true;
      lastState = '';
      lastRemaining = null;
      lastPct = 0;
      return;
    }
    el.hidden = false;

    var d = compute();
    var thresholdStr = fmt(d.thresholdCents, d.currency);

    if (d.flat) {
      el.setAttribute('data-ftt-state', 'flat');
      setLabel(COPY.flat || 'FREE SHIPPING OVER [threshold] AUD', '', thresholdStr);
      lastState = 'flat';
      return;
    }

    var state = d.done ? 'done' : (d.pct >= NEAR ? 'near' : 'far');
    var amountStr = fmt(d.remaining, d.currency);

    if (state === 'done') {
      setLabel(COPY.done || 'FREE SHIPPING UNLOCKED', amountStr, thresholdStr);
    } else {
      setLabel(
        state === 'near'
          ? (COPY.near || 'SO CLOSE — [amount] TO GO')
          : (COPY.far || '[amount] AWAY FROM FREE SHIPPING'),
        amountStr,
        thresholdStr
      );
      tweenAmount(lastRemaining, d.remaining, d.currency);
    }

    // Empty string is a deliberate "hide the chip", not a missing value.
    var targetTpl = COPY.target == null ? 'FREE OVER [threshold]' : COPY.target;
    target.textContent = String(targetTpl).split('[threshold]').join(thresholdStr);
    el.setAttribute('data-ftt-state', state);
    paint(d.pct, reseat);

    // The unlock moment — only when crossing the line, never on first paint.
    if (state === 'done' && lastState && lastState !== 'done' && !REDUCE) {
      el.classList.remove('ftt-sm--pop');
      void el.offsetWidth;
      el.classList.add('ftt-sm--pop');
      clearTimeout(popTimer);
      popTimer = setTimeout(function () {
        if (el) el.classList.remove('ftt-sm--pop');
      }, 1100);
    }

    lastState = state;
    lastRemaining = d.remaining;
  }

  function sync(reseat) {
    if (mount()) render(reseat);
  }

  /* -------------------- data -------------------- */

  function refresh() {
    if (inflight) return inflight;
    inflight = fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { cart = c; sync(false); })
      .catch(function () {})
      .then(function () { inflight = null; });
    return inflight;
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(refresh, delay || 70);
  }

  /* -------------------- hooks -------------------- */

  // 1. Wrap CartDrawer.renderContents so the meter is re-seated in the same
  //    task that wipes it — the browser never paints a frame without it.
  function hookDrawer() {
    var C = window.customElements && customElements.get('cart-drawer');
    if (!C || C.prototype.__fttSM) return;
    var orig = C.prototype.renderContents;
    if (typeof orig !== 'function') return;
    C.prototype.renderContents = function (parsed) {
      var out = orig.apply(this, arguments);
      try {
        if (parsed && typeof parsed.total_price === 'number') {
          cart = {
            total_price: parsed.total_price,
            item_count: typeof parsed.item_count === 'number' ? parsed.item_count : (cart ? cart.item_count : 1),
            currency: parsed.currency || (cart && cart.currency) || null
          };
        }
        el = null;
        sync(true);
      } catch (e) {}
      schedule(150);
      return out;
    };
    C.prototype.__fttSM = true;
  }

  // 2. Safety net: anything else that swaps the drawer markup.
  function observe() {
    if (!window.MutationObserver) return;
    var root = document.getElementById('shopify-section-cart-drawer') || document.body;
    new MutationObserver(function () {
      if (!el || !el.isConnected) sync(true);
    }).observe(root, { childList: true, subtree: true });
  }

  // 3. Catch cart writes from anywhere (sticky ATC, upsells, quick add).
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function hookFetch() {
    if (!window.fetch || window.fetch.__fttSM) return;
    var orig = window.fetch;
    var patched = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = orig.apply(this, arguments);
      if (CART_WRITE.test(url)) {
        p.then(function () { schedule(90); }).catch(function () {});
      }
      return p;
    };
    patched.__fttSM = true;
    window.fetch = patched;
  }

  function hookXHR() {
    var P = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!P || P.__fttSM) return;
    var open = P.open;
    P.open = function (method, url) {
      try {
        if (CART_WRITE.test(String(url))) {
          this.addEventListener('load', function () { schedule(90); });
        }
      } catch (e) {}
      return open.apply(this, arguments);
    };
    P.__fttSM = true;
  }

  function hookEvents() {
    ['cart:refresh', 'cart:updated', 'cart-drawer:open', 'cart:build'].forEach(function (evt) {
      document.addEventListener(evt, function () { schedule(60); });
    });
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.id === 'shopify-section-cart-drawer') {
        el = null;
        hookDrawer();
        schedule(40);
      }
    });
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('#cart-icon-bubble, .header__icon--cart, [href$="/cart"]')) {
        schedule(140);
      }
    }, true);
    window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(0); });
  }

  /* -------------------- boot -------------------- */

  function boot() {
    hookDrawer();
    hookFetch();
    hookXHR();
    hookEvents();
    observe();
    refresh();
    // The drawer element can upgrade after us on slower connections.
    var tries = 0;
    var poll = setInterval(function () {
      hookDrawer();
      if (mount() || ++tries > 20) {
        clearInterval(poll);
        sync(true);
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.FTTShipMeter = {
    refresh: refresh,
    sync: sync,
    compute: compute,
    get cart() { return cart; },
    get node() { return el; },
    config: CFG
  };
})();
